/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import useNotification from '../../../hooks/useNotification'
import useForm from '../../../hooks/useForm'
import {
  cleanPayload,
  isValidCommercialDate,
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../utils/objectUtils'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import { billingFields } from '../../../constants/forms/faturamento/billFields'
import Form from '../../../components/forms/Form'
import SectionHeader from '../../../components/forms/SectionHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import useBaseService from '../../../hooks/services/useBaseService'
import { useBillPageLogic } from './hooks/useBillPageLogic'
import { entities } from '../../../constants/entities'
import BillSpreadsheet from './components/BillSpreadsheet'
import { Alert, Box } from '@mui/material'
import { useSpreadsheetAutoSave } from './hooks/useSpreadsheetAutoSave'
import { useBillingExtractor } from './hooks/useBillingExtractor'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BillingEnum } from '../../../enums/billing-enum'
import Button from '../../../components/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@fortawesome/free-solid-svg-icons'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import ContractInfoCard from './components/ContractInfoCard'

const CreateBillPage = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const [billingSection] = billingFields
  const [billingFieldsState, setBillingFieldsState] = useState(
    billingSection.fields
  )
  const [univerAPI, setUniverAPI] = useState(null)
  const queryClient = useQueryClient()

  const [resetKey, setResetKey] = useState(0)

  const initialValues = useMemo(
    () => setDefaultFieldValues([billingSection]),
    [billingSection]
  )

  const { formData, handleChange, setFormData, resetForm } = useForm(
    initialValues,
    [billingSection]
  )

  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
  } = useBaseService(navigate)

  const { contract, separate, selects } = useBillPageLogic({
    mode: 'create',
    billingFields: billingFieldsState,
    setBillingFields: setBillingFieldsState,
    formData,
    setFormData,
    resetForm,
    baseServiceGet,
  })

  const billType = selects.billTypeSelect.selectedValue?.value
  const organizationId = selects.organizationSelect.selectedValue?.value

  const byContract = billType === 'by_contract'
  const isSeparate = billType === 'separate'

  const shouldFetchBillingData =
    !!organizationId &&
    ((byContract && !!contract?.id) ||
      (isSeparate && !!selects.customerSelect.selectedValue?.value))

  // <-- Hook de extração híbrida
  const { extractBillingData } = useBillingExtractor(billType)

  // <-- Gerar chave de rascunho contextual
  const draftKey = useMemo(() => {
    const customerId = selects.customerSelect.selectedValue?.value
    const contractId = selects.contractSelect.selectedValue?.value

    if (!billType || !customerId) {
      return null
    }

    if (billType === 'by_contract') {
      if (!contractId) return null
      return `bill_draft_by_contract_${contractId}_${customerId}`
    } else if (billType === 'separate') {
      return `bill_draft_separate_${customerId}`
    }

    return null
  }, [
    selects.billTypeSelect.selectedValue?.value,
    selects.customerSelect.selectedValue?.value,
    selects.contractSelect.selectedValue?.value,
  ])

  const { loadDraft, clearDraft } = useSpreadsheetAutoSave(
    univerAPI,
    draftKey,
    !!draftKey
  )

  const handleResetDraft = useCallback(() => {
    clearDraft()
    setResetKey((prev) => prev + 1)
    queryClient.invalidateQueries({ queryKey: ['billingData'] })
    showNotification('info', 'Rascunho resetado com sucesso!')
  }, [clearDraft, queryClient, showNotification])

  const handleSpreadsheetReady = useCallback(
    (api) => {
      console.log('✅ Planilha pronta!')
      setUniverAPI(api)

      if (draftKey) {
        const draftSnapshot = loadDraft()
        if (draftSnapshot) {
          console.log(`📄 Carregando rascunho para contexto: ${draftKey}`)
          try {
            const currentWorkbook = api.getActiveWorkbook()
            if (currentWorkbook) {
              api.disposeUnit(currentWorkbook.getId())
            }
            api.createWorkbook(draftSnapshot)
            showNotification('info', 'Rascunho carregado automaticamente')
          } catch (error) {
            console.error('❌ Erro ao carregar rascunho:', error)
          }
        }
      }
    },
    [draftKey, loadDraft, showNotification]
  )

  const previousDraftKey = useRef(null)
  useEffect(() => {
    if (previousDraftKey.current && previousDraftKey.current !== draftKey) {
      console.log(
        `🔄 Contexto mudou de ${previousDraftKey.current} para ${draftKey}`
      )
    }
    previousDraftKey.current = draftKey
  }, [draftKey])

  const createBillingMutation = useMutation({
    mutationFn: async (data) => {
      const response = await baseServicePost(entities.billing.create, data)
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        resetForm()

        queryClient.invalidateQueries({ queryKey: ['billingData'] })

        clearDraft()
      }
    },
  })

  const handleSubmit = async () => {
    try {
      if (isEmptySeparateBilling) return
      if (!univerAPI) {
        showNotification('error', 'Planilha não está pronta. Aguarde...')
        return
      }

      // === 1. Extrai snapshot da planilha ===
      const fWorkbook = univerAPI.getActiveWorkbook()
      if (!fWorkbook) {
        showNotification('error', 'Erro ao acessar planilha')
        return
      }

      const spreadsheetSnapshot = fWorkbook.save()

      // === 2. Extrai dados estruturados ===
      const extractedData = extractBillingData(univerAPI, billType, formData)

      const { items, totals, metadata } = extractedData

      // === 3. Validações ===
      if (items.length === 0) {
        showNotification(
          'warning',
          'Nenhum item encontrado na planilha. Verifique se os dados estão preenchidos.'
        )
        return
      }

      const cleanedFormData = cleanPayload(formData)

      const hasDates =
        cleanedFormData.billing.start_date && cleanedFormData.billing.end_date

      if (!hasDates) {
        showNotification(
          'warning',
          'Preencha todos os campos de data de inicio e fim dos items no formulário'
        )
        return
      }

      // === 4. Monta payload final ===
      const status = !cleanedFormData.billing.number
        ? BillingEnum.DRAFT
        : BillingEnum.PENDING

      const payload = {
        billing: {
          ...cleanedFormData.billing,
          bill_type: billType,
          number:
            cleanedFormData.billing.number != null
              ? String(cleanedFormData.billing.number).trim()
              : null,
          contract_id: contract?.id || null,
          customer_id: selects.customerSelect.selectedValue?.value || null,
          status,
          organization_id: cleanedFormData.billing?.organization_id,
        },
        items: items.map((item) => ({
          alias: item.alias,
          description: item.description,
          quantity: item.quantity,
          price: item.price,
        })),
        spreadsheet_snapshot: spreadsheetSnapshot,
        metadata: {
          ...metadata,
          form_data: cleanedFormData,
          totals: totals,
        },
      }

      console.log('🚀 Payload final CORRETO:', payload)

      // === 5. Envia para o backend ===
      await createBillingMutation.mutateAsync(payload)
    } catch (error) {
      console.error('Erro ao enviar faturamento:', {
        status: error.status,
        message: error.message,
      })
    }
  }

  const handleBack = useCallback(() => {
    navigate('/faturamento/faturar/')
  }, [navigate])

  const { data: billingResponse, isLoading: isLoadingBilling, isSuccess: isBillingDataSuccess } =
    useQueryWithLoader({
      queryKey: [
        'billingData',
        billType,
        organizationId,
        contract?.id,
        selects.customerSelect.selectedValue?.value,
        formData.billing.start_date,
        formData.billing.end_date,
      ],
      queryFn: async () => {
        if (
          !shouldFetchBillingData ||
          !organizationId ||
          !selects.customerSelect.selectedValue?.value ||
          !formData.billing.start_date ||
          !formData.billing.end_date
        ) {
          return null
        }

        const filters = {
          contract_id: byContract ? contract?.id : null,
          organization_id: organizationId,
          bill_type: billType,
          recipient_customer_id: selects.customerSelect.selectedValue?.value,
          start_date: formData.billing.start_date,
          end_date: formData.billing.end_date,
        }

        const response = await baseServiceGet(entities.billing.data.get, {
          filters,
        })

        return response.result
      },
      enabled:
        shouldFetchBillingData &&
        !!organizationId &&
        !!selects.customerSelect.selectedValue?.value &&
        isValidCommercialDate(formData.billing.start_date) &&
        isValidCommercialDate(formData.billing.end_date),
      staleTime: 5 * 60 * 1000,
    })

  const {
    data: contractServicesResponse,
    isLoading: isLoadingContractServices,
  } = useQueryWithLoader({
    queryKey: ['contractServicesForBilling', contract?.id],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.jobs(contract.id),
        { page: 1, perPage: 1000 }
      )
      const services = response.result?.data ?? response.result ?? []
      return services.map((service) => ({
        ...service,
        quantity: Number(service.quantity) || 0,
        price: Number(service.price) || 0,
      }))
    },
    enabled: byContract && !!contract?.id,
  })

  // Extrai os dados da resposta
  const contractItems = billingResponse?.contract_items || []
  const contractServices = contractServicesResponse || []
  const typeRentedEquipments = billingResponse?.rented_types || []
  const exchangeRentedEquipments = billingResponse?.exchange_rented_types || []
  const equipments = billingResponse?.equipments || []
  const monthlyHistory = billingResponse?.monthly_history || []

  // Converte monthlyHistory para o formato esperado pelo BillSpreadsheet
  const monthlyEquipments = monthlyHistory.map((month) => ({
    reference: month.reference,
    month: month.month,
    equipments: month.equipments,
  }))

  const monthlyData = useMemo(() => {
    const result = {}
    monthlyHistory.forEach((month) => {
      result[month.reference] = {
        contractItems: month.contract_items || [],
        rentedEquipments: month.rented_types || [],
        exchangeRentedEquipments: month.exchange_rented_types || [],
      }
    })
    return result
  }, [monthlyHistory])

  // Condição de carregamento simplificada
  const isLoaded =
    shouldFetchBillingData && billingResponse !== undefined && !isLoadingBilling &&
    (!byContract || (!isLoadingContractServices && contractServicesResponse !== undefined))
  const isEmptySeparateBilling =
    isSeparate &&
    isBillingDataSuccess &&
    !isLoadingBilling &&
    Array.isArray(billingResponse?.rented_types) &&
    billingResponse.rented_types.length === 0 &&
    Array.isArray(billingResponse?.equipments) &&
    billingResponse.equipments.length === 0

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Faturar"
        showBackButton={true}
        backUrl="/faturamento/faturar/"
      />
      <div className="container-fluid p-1" style={{ position: 'relative' }}>
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Realizar faturamento"
          textLoadingSubmit="Realizando faturamento..."
          handleBack={handleBack}
          disabledSubmitButton={!isLoaded || isEmptySeparateBilling}>
          {() => (
            <Box key={billingSection.section} sx={{ mb: 2 }}>
              <SectionHeader section={billingSection.section} />

              <SimpleBody
                fields={billingFieldsState}
                formErrors={normalizeFormErrors(formErrors)}
                formData={formData}
                handleFieldChange={handleChange}
                getOptions={selects.getOptions}
                getSelectedValue={selects.getSelectedValue}
                getSelectMeta={selects.getSelectMeta}
              />
            </Box>
          )}
        </Form>
        {isEmptySeparateBilling && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Não há itens avulsos entregues para este cliente nesta organização no período selecionado.
          </Alert>
        )}
        {isLoaded && !isEmptySeparateBilling && (
          <Box sx={{ mt: 4 }}>
            <ContractInfoCard contractInfo={contract.amount} />
            <Box display="flex" justifyContent="flex-end" my={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleResetDraft}
                disabled={!draftKey}
                text="Resetar Rascunho"
                endIcon={<FontAwesomeIcon icon={faRefresh} />}
              />
            </Box>

            <BillSpreadsheet
              key={[
                billType,
                contract?.id,
                separate?.recipient_customer_id,
                resetKey,
              ].join('-')}
              contractItems={contractItems}
              contractServices={contractServices}
              rentedEquipments={typeRentedEquipments}
              exchangeRentedEquipments={exchangeRentedEquipments}
              equipmentsDetails={equipments}
              monthlyData={monthlyData}
              monthlyEquipments={monthlyEquipments}
              startDate={formData.billing.start_date}
              billType={billType}
              onReady={handleSpreadsheetReady}
            />
          </Box>
        )}
      </div>
    </MainLayout>
  )
}

export default CreateBillPage
